// The office's one table. Every register on the desk renders through this, so
// they read as one surface — and as the grids in app-commercial and app-magic
// read: a header band that stays put while you scroll, sortable headers with an
// arrow, a search box with a count, rows a hairline apart on a zebra wash,
// numbers right-aligned and tabular.
//
// Built on TanStack Table, which is headless: the markup stays a real <table>,
// so screen readers keep table navigation, the caption and header scopes. That
// is why this is not AG Grid, which the other two apps use (Amit, 2026-10-01:
// "TanStack, styled to match").
//
// ⚠ ORDER. Rows render in the order they are given until a reader clicks a
// header, and the third click returns to it. Registers whose order is the
// argument (Goods in: goods on site without cover first) pass rows in that
// order; sorting is the reader's choice, never the page's.
import { Button } from '@/components/ui/button';
import { useState, type MouseEvent, type ReactNode } from 'react';
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type PaginationState,
  type RowData,
  type SortingState,
  type Updater,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronsUpDown, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { fieldClass, officeFocus } from '../shells/OfficeShell';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Right for quantities and counts; numbers are compared down a column. */
    align?: 'left' | 'right' | 'center';
    /** The mono face with tabular figures: ids, quantities, limits. */
    mono?: boolean;
    /** Keep the cell on one line. */
    nowrap?: boolean;
    /** A width for the column, e.g. '7rem'. */
    width?: string;
    /** Render the cell as the row's header (`<th scope="row">`). One per table. */
    rowHeader?: boolean;
    /** Extra classes on the column's header and every cell, e.g. 'hidden xl:table-cell' for a
     *  column a page folds into another below a width (UI/UX audit, 31 Oct 2026, UX-15). The
     *  column's content must then be said somewhere else at those widths. */
    cellClass?: string;
  }
}

export type RowAccent = 'bad' | 'warn' | null;

export interface DataTableProps<T> {
  data: T[];
  // Columns of mixed value types are the normal case; TanStack's own docs type
  // this as `ColumnDef<T, any>`.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[];
  /** Read by screen readers only. Say what the rows are and in what order. */
  caption: string;
  getRowId?: (row: T, index: number) => string;
  /** A bar at the row's left edge for rows that need someone. Red is spent only
   *  on goods or records that are wrong now; amber on what is due. */
  rowAccent?: (row: T) => RowAccent;
  /** The row whose detail the page is showing (Runs opens one below the list). */
  isRowSelected?: (row: T) => boolean;
  /** Where clicking anywhere on a row goes. The row should still carry a real
   *  link in a cell — this is a convenience for the mouse, not the way in. */
  rowHref?: (row: T) => string | null;
  /** Shown when there are no rows, nothing is loading and the read did not fail. */
  empty?: ReactNode;
  loading?: boolean;
  /** The server's sentence when the read FAILED. Rule 15: shown in the table's place with Try
   *  again, and `empty` is never shown beside it - "could not read" is not "nothing here". */
  error?: string | null;
  onRetry?: () => void;
  /** A search box over every column's text; the string is its placeholder. */
  search?: string;
  /** Controls rendered beside the search box. */
  toolbar?: ReactNode;
  /** Start sorted; absent means the order given. */
  initialSorting?: SortingState;
  /** The scroll area's height. The header sticks inside it. */
  maxHeight?: string;
  /** Rows per page. Unset = every row. Set, and once the (filtered) rows exceed it, a
   *  footer says "Showing 1–50 of 312" with Previous / Next; the page returns to 1
   *  whenever the search or the sort changes. The count line keeps the total. */
  pageSize?: number;
  /** Keep the first column in view while the table scrolls sideways (`sticky left-0`, on its
   *  header and every row's cell). For registers wider than an iPad's content column: the row's
   *  name stays beside the columns that matter. Opt in; the column should be the row header. */
  stickyFirst?: boolean;
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

export function DataTable<T>({
  data,
  columns,
  caption,
  getRowId,
  rowAccent,
  isRowSelected,
  rowHref,
  empty,
  loading = false,
  error = null,
  onRetry,
  search,
  toolbar,
  initialSorting,
  maxHeight = 'calc(100dvh - 15rem)',
  pageSize,
  stickyFirst = false,
}: DataTableProps<T>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting ?? []);
  const [globalFilter, setGlobalFilter] = useState('');
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: pageSize ?? 0 });
  const navigate = useNavigate();
  const paged = pageSize !== undefined && pageSize > 0;

  // A new search or sort is a new list: it starts on page 1 (item 22, UI/UX audit 27 Oct 2026).
  const firstPage = () => setPagination((p) => ({ ...p, pageIndex: 0 }));
  const onSortingChange = (u: Updater<SortingState>) => {
    setSorting(u);
    firstPage();
  };
  const onGlobalFilterChange = (u: Updater<string>) => {
    setGlobalFilter(u);
    firstPage();
  };

  const table = useReactTable({
    data,
    columns,
    getRowId,
    state: { sorting, globalFilter, ...(paged ? { pagination } : {}) },
    onSortingChange,
    onGlobalFilterChange,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // Only when a page size is given: without it the row model is the filtered one, whole.
    ...(paged ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    globalFilterFn: 'includesString',
    // asc → desc → the order given. The given order can be the argument.
    enableSortingRemoval: true,
  });

  const rows = table.getRowModel().rows;
  const matching = table.getFilteredRowModel().rows.length;
  const width = table.getVisibleLeafColumns().length;
  // The footer only once there is more than one page of what matches.
  const pageFrom = paged ? pagination.pageIndex * pagination.pageSize + 1 : 1;
  const pageTo = paged ? Math.min(matching, pageFrom + pagination.pageSize - 1) : matching;
  const showPager = paged && matching > pagination.pageSize;

  function onRowClick(e: MouseEvent<HTMLTableRowElement>, href: string | null) {
    if (!href || e.defaultPrevented) return;
    // A click on anything interactive inside the row is that thing's click.
    if ((e.target as HTMLElement).closest('a, button, input, select, textarea, label')) return;
    if (window.getSelection()?.toString()) return; // selecting text is not a click
    navigate(href);
  }

  return (
    <div className="flex flex-col gap-(--s2)">
      {search !== undefined || toolbar ? (
        <div className="flex flex-wrap items-center gap-(--s2)">
          {search !== undefined ? (
            <label className="relative flex items-center">
              <span className="sr-only">Search the table</span>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute left-[12px] h-[16px] w-[16px] text-(--muted)"
              />
              <input
                type="search"
                value={globalFilter}
                onChange={(e) => onGlobalFilterChange(e.target.value)}
                placeholder={search}
                className={`${fieldClass} ${officeFocus} w-[18rem] pl-[36px]`}
              />
            </label>
          ) : null}
          {toolbar}
          {/* No count while there is nothing read yet (UI/UX audit, 31 Oct 2026, UX-10; rule 15).
              "0 rows" beside the loading skeleton - or beside "Could not read this list" - is a
              count of a list nobody has seen. Rows already on screen keep their count through a
              reload or a failed re-read: those rows were read. */}
          {(loading || error) && data.length === 0 ? null : (
            <span className="ml-auto text-[12px] text-(--muted)" aria-live="polite">
              {matching === data.length ? `${data.length} rows` : `${matching} of ${data.length} rows`}
            </span>
          )}
        </div>
      ) : null}

      <div
        className="overflow-auto rounded-(--radius) border border-(--border) bg-(--card) shadow-card"
        style={{ maxHeight }}
      >
        <table className="data-table w-full border-separate border-spacing-0 text-(length:--fs-sm)">
          <caption className="sr-only">{caption}</caption>
          <thead>
            {table.getHeaderGroups().map((group) => (
              <tr key={group.id}>
                {group.headers.map((header, h) => {
                  const meta = header.column.columnDef.meta;
                  // The corner cell sticks both ways, so it sits above the header band (z-1) that
                  // slides under it (UI/UX audit, 31 Oct 2026, UX-15).
                  const pin = stickyFirst && h === 0;
                  const sorted = header.column.getIsSorted();
                  const label = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : undefined}
                      style={{ width: meta?.width }}
                      data-sticky={pin ? 'true' : undefined}
                      className={`sticky top-0 ${pin ? 'left-0 z-2 border-r' : 'z-1'} whitespace-nowrap border-b border-(--border-strong) bg-(--subtle) px-[12px] py-[9px] text-[11px] font-semibold uppercase tracking-[0.06em] text-(--muted) ${ALIGN[meta?.align ?? 'left']} ${meta?.cellClass ?? ''}`}
                    >
                      {header.column.getCanSort() ? (
                        <button
                          type="button"
                          onClick={header.column.getToggleSortingHandler()}
                          className={`inline-flex items-center gap-[4px] rounded-[4px] uppercase tracking-[0.06em] hover:text-(--fg) ${officeFocus} ${meta?.align === 'right' ? 'flex-row-reverse' : ''}`}
                          title="Sort; a third click restores the original order"
                        >
                          {label}
                          {sorted === 'asc' ? (
                            <ArrowUp aria-hidden="true" className="h-[12px] w-[12px]" />
                          ) : sorted === 'desc' ? (
                            <ArrowDown aria-hidden="true" className="h-[12px] w-[12px]" />
                          ) : (
                            <ChevronsUpDown aria-hidden="true" className="h-[12px] w-[12px] opacity-40" />
                          )}
                        </button>
                      ) : (
                        label
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && rows.length === 0
              ? [0, 1, 2].map((i) => (
                  <tr key={`loading-${i}`} aria-hidden="true">
                    {Array.from({ length: width }, (_, c) => (
                      <td key={c} className="border-b border-(--border) px-[12px] py-[10px]">
                        <span className="block h-[12px] w-3/4 animate-pulse rounded bg-(--subtle)" />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {rows.map((row) => {
              const href = rowHref?.(row.original) ?? null;
              return (
                <tr
                  key={row.id}
                  data-accent={rowAccent?.(row.original) ?? undefined}
                  data-selected={isRowSelected?.(row.original) ? 'true' : undefined}
                  // data-open: the row opens somewhere, so the pointer, a stronger hover wash and
                  // the row header's underline say so (index.css; item 23, UI/UX audit 27 Oct 2026).
                  data-open={href ? 'true' : undefined}
                  onClick={href ? (e) => onRowClick(e, href) : undefined}
                  className={href ? 'cursor-pointer' : undefined}
                >
                  {row.getVisibleCells().map((cell, c) => {
                    const meta = cell.column.columnDef.meta;
                    // Sticky first column (UI/UX audit, 31 Oct 2026, UX-15): no z-index, so it stays
                    // under the header band (z-1) when the table scrolls down, and over the other
                    // cells (which are not positioned) when it scrolls sideways. Its background is
                    // opaque in index.css ([data-sticky]) so the cells it covers do not show through.
                    const pin = stickyFirst && c === 0;
                    const className = [
                      'border-b border-(--border) px-[12px] py-[8px] align-top',
                      pin ? 'sticky left-0 border-r' : '',
                      ALIGN[meta?.align ?? 'left'],
                      meta?.mono ? 'font-mono tabular-nums' : '',
                      meta?.nowrap ? 'whitespace-nowrap' : '',
                      meta?.cellClass ?? '',
                    ].join(' ');
                    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                    const sticky = pin ? 'true' : undefined;
                    return meta?.rowHeader ? (
                      <th key={cell.id} scope="row" data-sticky={sticky} className={`${className} font-semibold`}>
                        {content}
                      </th>
                    ) : (
                      <td key={cell.id} data-sticky={sticky} className={className}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {!loading && error ? (
              <tr>
                <td colSpan={width} className="px-[12px] py-[24px] text-center">
                  <div role="alert" className="flex flex-col items-center gap-(--s2)">
                    <span className="font-semibold text-(--warn)">Could not read this list: {error}</span>
                    <span className="text-(--muted)">This is not the same as "none".</span>
                    {onRetry ? (
                      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
                        Try again
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ) : !loading && rows.length === 0 ? (
              <tr>
                <td colSpan={width} className="px-[12px] py-[24px] text-center text-(--muted)">
                  {data.length > 0 ? 'Nothing matches the search.' : (empty ?? 'Nothing to show.')}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {showPager ? (
        <div className="flex flex-wrap items-center gap-(--s2)">
          <span className="text-[12px] text-(--muted)" aria-live="polite">
            Showing {pageFrom}–{pageTo} of {matching}
          </span>
          <div className="ml-auto flex items-center gap-(--s2)">
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label="Previous page"
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label="Next page"
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default DataTable;
